import { useEditorStore } from "../store/editor-store";
import type { WorkspaceFolder } from "@magic-md/engine-client/protocol";
import { t as translate } from "@magic-md/i18n";

export const EXPLORER_REVEAL_EVENT = "magic-md:explorer-reveal";

function tr(key: string): string {
  return translate("editor", key);
}

export interface ExplorerRevealDetail {
  path: string;
}

function normalizePath(path: string): string {
  return path.replace(/\\/g, "/").replace(/\/+$/, "");
}

function dirname(path: string): string {
  const normalized = normalizePath(path);
  const index = normalized.lastIndexOf("/");
  if (index <= 0) return normalized;
  return normalized.slice(0, index);
}

function findWorkspaceRoot(
  targetPath: string,
  workspaceFolders: WorkspaceFolder[],
): string | null {
  const normalizedTarget = normalizePath(targetPath);
  return workspaceFolders
    .map((folder) => normalizePath(folder.path))
    .filter(
      (root) => normalizedTarget === root || normalizedTarget.startsWith(`${root}/`),
    )
    .sort((a, b) => b.length - a.length)[0] ?? null;
}

export function buildExplorerRevealExpansionPaths(
  targetPath: string,
  workspaceFolders: WorkspaceFolder[],
): string[] {
  const normalizedTarget = normalizePath(targetPath);
  const root = findWorkspaceRoot(normalizedTarget, workspaceFolders);
  if (!root) return [dirname(normalizedTarget)];

  const parentDir = dirname(normalizedTarget);
  const paths = [root];
  if (parentDir === root) return paths;

  const relativeParent = parentDir
    .slice(root.length)
    .replace(/^\/+/, "")
    .split("/")
    .filter(Boolean);

  let current = root;
  for (const segment of relativeParent) {
    current = `${current}/${segment}`;
    paths.push(current);
  }
  return paths;
}

function dispatchExplorerReveal(path: string): void {
  if (typeof window === "undefined") return;
  const detail: ExplorerRevealDetail = { path: normalizePath(path) };
  window.dispatchEvent(
    new CustomEvent<ExplorerRevealDetail>(EXPLORER_REVEAL_EVENT, { detail }),
  );
}

function scheduleExplorerRevealDispatch(path: string): void {
  if (typeof window === "undefined") return;
  const dispatch = () => dispatchExplorerReveal(path);
  if (typeof window.requestAnimationFrame === "function") {
    window.requestAnimationFrame(() => window.requestAnimationFrame(dispatch));
    return;
  }
  window.setTimeout(dispatch, 0);
}

export async function revealPathInExplorer(path: string): Promise<void> {
  const targetPath = normalizePath(path);
  const state = useEditorStore.getState();
  useEditorStore.setState({ sidebarOpen: true, sidebarView: "explorer" });

  const expansionPaths = buildExplorerRevealExpansionPaths(
    targetPath,
    state.workspaceFolders,
  );
  for (const expansionPath of expansionPaths) {
    await useEditorStore.getState().expandTreeNode(expansionPath);
  }

  scheduleExplorerRevealDispatch(targetPath);
  useEditorStore.getState().setStatusMessage(tr("explorerReveal.located"), 2000);
}
